import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}

const ColorObjects: Record<string, string[]> = {
    Red: ["🍎", "🚗", "🎈", "🥊", "🦞", "🌹", "🍓", "📕"],
    Blue: ["💙", "🚙", "🌊", "🧢", "🐟", "🦋", "🔷", "📘"],
    Green: ["🍏", "🌳", "🐸", "🥒", "🍀", "🦜", "🧩", "📗"],
    Yellow: ["🌟", "🍌", "🚖", "🌻", "🐤", "🧀", "⚠️", "📒"],
    Orange: ["🦊", "🏀", "🍊", "📙", "🌅", "🦁", "🥕", "🔶"]
};


const MatchColorObject: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect(); setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Fetch questions dynamically
    const fetchQuestions = () => {
        const usedKeys: string[] = [];
        const homeKeys = Object.keys(ColorObjects);
        const shuffledKeys = shuffleArray([...homeKeys]);
        const selectedKeys = shuffledKeys.slice(0, 4);
        const newQuestions: ImagePair[] = [];

        const suffledNumbers = shuffleArray([...selectedKeys]);
        //console.log(suffledNumbers)
        selectedKeys.map((key, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: selectedKeys.indexOf(suffledNumbers[index]) + 1,
                leftImage: ColorObjects[key][Math.floor(Math.random() * ColorObjects[key].length)],
                rightImage: ColorObjects[suffledNumbers[index]][Math.floor(Math.random() * ColorObjects[suffledNumbers[index]].length)],
            });
        })

        setQuestions(newQuestions);
    };

    useEffect(() => {
        resetConnections();
        fetchQuestions();
        document.title = "Match the Color – Nursery Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        resetConnections();
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 2000);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids explore colors with the "Match the Color" activity on Mamas Guidance—an engaging game that teaches children to match objects with their corresponding colors, enhancing color recognition and early cognitive development.' />
            </Helmet>
            {!showImage ? (
                <>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Match the Color</h1>

                            <p>
                                Make color learning exciting with our vibrant <strong>Match the Color</strong> worksheet! Designed for young learners in nursery and preschool, this worksheet helps children <strong>match everyday objects to their correct colors</strong>.
                            </p>

                            <h2>🖍️ What’s in the Worksheet?</h2>
                            <p>
                                Children will see familiar items like a <b><em>banana, apple, sun, cloud, grass, ball, or butterfly</em></b> on one side, and colored blocks or names on the other. They’ll draw lines to match each object to its correct color — promoting <strong>visual recognition</strong> and <strong>memory recall</strong>.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify primary and secondary colors (<strong>red, blue, green, yellow, etc</strong>.)</li>
                                <li>Associate colors with commonly seen objects</li>
                                <li>Improve hand-eye coordination and fine motor skills</li>
                                <li>Build foundational vocabulary related to colors</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Ask your child to name each object aloud.</li>
                                <li>Talk about the color of the object in real life.</li>
                                <li>Help them draw a line connecting the object to the correct color.</li>
                                <li>Repeat the activity using toys or household items for reinforcement.</li>
                            </ul>



                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Guess the Area</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Pick Color</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Guess Community Helper</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Is Beneficial</h2>
                            <p>
                                Matching objects to their colors helps build a child’s understanding of their surroundings. It also encourages <strong>active thinking, attention to detail, and early categorization skills</strong>, essential for foundational development.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Match the Color</h1>

                        <p>
                            Make color learning exciting with our vibrant <strong>Match the Color</strong> worksheet! Designed for young learners in nursery and preschool, this worksheet helps children <strong>match everyday objects to their correct colors</strong>.
                        </p>

                        <h2>🖍️ What’s in the Worksheet?</h2>
                        <p>
                            Children will see familiar items like a <em>banana, apple, sun, cloud, grass, ball, or butterfly</em> on one side, and colored blocks or names on the other. They’ll draw lines to match each object to its correct color — promoting <strong>visual recognition</strong> and <strong>memory recall</strong>.
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Identify primary and secondary colors (red, blue, green, yellow, etc.)</li>
                            <li>Associate colors with commonly seen objects</li>
                            <li>Improve hand-eye coordination and fine motor skills</li>
                            <li>Build foundational vocabulary related to colors</li>
                        </ul>

                        <h2>👩‍🏫 How to Use the Worksheet</h2>
                        <ul>
                            <li>Ask your child to name each object aloud.</li>
                            <li>Talk about the color of the object in real life.</li>
                            <li>Help them draw a line connecting the object to the correct color.</li>
                            <li>Repeat the activity using toys or household items for reinforcement.</li>
                        </ul>



                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Guess the Area</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Pick Color</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Guess Community Helper</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧠 Why This Activity Is Beneficial</h2>
                        <p>
                            Matching objects to their colors helps build a child’s understanding of their surroundings. It also encourages <strong>active thinking, attention to detail, and early categorization skills</strong>, essential for foundational development.
                        </p>
                    </article>
                    <div ref={containerRef} className="contentPart additionWord matchImage matchColor match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>{/* SVG for drawing lines */}
                        <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                            {connections.map((conn, index) => (
                                <line
                                    key={index}
                                    x1={conn.x1}
                                    y1={conn.y1}
                                    x2={conn.x2}
                                    y2={conn.y2}
                                    stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                    strokeWidth="3"
                                />
                            ))}
                        </svg>
                        <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%", height: "100%" }}>
                            <div className="left-column">
                                {questions.map((item) => (
                                    <div
                                        key={item.left_id}
                                        className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                        onClick={(e) => handleLeftClick(e, item.left_id)}
                                        style={{ cursor: "pointer", padding: "10px" }}
                                    >
                                        {item.leftImage}
                                    </div>
                                ))}
                            </div>
                            <div className="right-column">
                                {questions.map((item) => (
                                    <div key={item.left_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                        {item.rightImage}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-2 mb-4 text-center">
                            <div className="btn hero-btn" onClick={nextQuestion}>Next Question</div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} />
                </div>
            )}
        </>
    );
};

export default MatchColorObject;
